interface INativeFieldTypes {
    string: string;
    boolean: boolean;
}

export type TNativeMessageFields = Record<string, keyof INativeFieldTypes>;

export type TNativeMessagePayload<Fields extends TNativeMessageFields> = { [Field in keyof Fields]: INativeFieldTypes[Fields[Field]] };

export interface INativeMessage<Type extends string, Fields extends TNativeMessageFields> {
    type: Type;
    parse: (detail: unknown) => TNativeMessagePayload<Fields> | null;
}

export type TNativeMessagePayloadOf<Message> = Message extends INativeMessage<string, infer Fields> ? TNativeMessagePayload<Fields> : never;

interface IDefineNativeMessageParams<Type extends string, Fields extends TNativeMessageFields> {
    type: Type;
    fields: Fields;
}

export const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

export const defineNativeMessage = <Type extends string, Fields extends TNativeMessageFields>({
    type,
    fields,
}: IDefineNativeMessageParams<Type, Fields>): INativeMessage<Type, Fields> => {
    const fieldEntries = Object.entries(fields);

    const parse = (detail: unknown) => {
        if (!isRecord(detail) || detail.type !== type) {
            return null;
        }

        const hasValidFields = fieldEntries.every(([field, fieldType]) => typeof detail[field] === fieldType);
        if (!hasValidFields) {
            return null;
        }

        return Object.fromEntries(fieldEntries.map(([field]) => [field, detail[field]])) as TNativeMessagePayload<Fields>;
    };

    return { type, parse };
};
